import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AFFILIATE, IMG } from "../lib/api";
import AffiliateButton from "../components/AffiliateButton";
import EmailCapture from "../components/EmailCapture";
import { Crown, Users, PiggyBank, Star, MapPin, ArrowRight } from "lucide-react";

const hotels = [
  {
    tag: "Best Overall Stay",
    icon: Star,
    name: "The Embankment Hotel",
    blurb:
      "A beautifully restored Georgian hotel on the banks of the Great Ouse — moments from the town centre and a short drive from the park.",
    distance: "1.8 miles from Universal",
    price: "From £149/night",
    img: IMG.luxuryHotel1,
    cta: "Check Availability",
    href: AFFILIATE.booking,
    testId: "hotel-best-overall",
    color: "#FF8C00",
  },
  {
    tag: "Best for Families",
    icon: Users,
    name: "Riverside Family Suites",
    blurb:
      "Spacious family rooms, on-site pool and buffet breakfasts geared for kids. Ideal for early park starts and tired legs afterwards.",
    distance: "2.4 miles from Universal",
    price: "From £179/night",
    img: IMG.familyRoom,
    cta: "View Family Rooms",
    href: AFFILIATE.bookingFamily,
    testId: "hotel-family",
    color: "#2AB7E8",
  },
  {
    tag: "Best Budget Option",
    icon: PiggyBank,
    name: "Great Ouse Inn",
    blurb:
      "Clean, modern rooms from under £70 — a smart pick if you're spending most of your trip at the park and want to save for experiences.",
    distance: "3.1 miles from Universal",
    price: "From £69/night",
    img: IMG.budgetHotel,
    cta: "View Prices",
    href: AFFILIATE.bookingBudget,
    testId: "hotel-budget",
    color: "#2AB7E8",
  },
  {
    tag: "Best Luxury Stay",
    icon: Crown,
    name: "The Bedford Manor",
    blurb:
      "A five-star country manor hotel with a spa, two restaurants and landscaped gardens. The after-park treat your partner deserves.",
    distance: "4.6 miles from Universal",
    price: "From £329/night",
    img: IMG.luxuryHotel2,
    cta: "Check Rooms",
    href: AFFILIATE.bookingLuxury,
    testId: "hotel-luxury",
    color: "#FFB800",
  },
];

const Hotels = () => {
  useEffect(() => {
    document.title = "Hotels Near Universal Studios Bedford — Best Places to Stay 2026 | Visit Bedford";
  }, []);

  return (
    <div data-testid="hotels-page">
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
        <img src={IMG.luxuryHotel1} alt="Luxury hotel room" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-site pb-16 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                Hotels
              </div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] text-balance mb-5">
                Hotels Near Universal Studios Bedford
              </h1>
              <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-8">
                The best places to stay in 2026 — curated by location, value and convenience.
              </p>
              <AffiliateButton href={AFFILIATE.booking} variant="primary" testId="hotels-hero-cta">
                Check Availability Near Universal Bedford
              </AffiliateButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#FAFAFA] py-16 md:py-24">
        <div className="container-site grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">
              Our 2026 picks
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B2C5B] leading-tight">
              Four hotels. One question answered: where should you stay?
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-lg leading-relaxed text-slate-700 space-y-5">
            <p>
              Choosing the right hotel can make or break your Universal Studios trip. Too far from the park and you'll lose hours commuting; too close and you'll pay a premium for a cookie-cutter room.
            </p>
            <p>
              We've road-tested the shortlist. Here are our four favourites — one for every kind of traveller.
            </p>
          </div>
        </div>
      </section>

      {/* Hotel Cards */}
      <section className="bg-[#FAFAFA] pb-20 md:pb-28">
        <div className="container-site space-y-16 md:space-y-24">
          {hotels.map((h, i) => (
            <motion.article
              key={h.tag}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              data-testid={h.testId}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-7 relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-[16/11] group">
                <img src={h.img} alt={h.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 text-xs font-bold tracking-[0.2em] uppercase" style={{ color: h.color }}>
                  <h.icon className="w-4 h-4" /> {h.tag}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-3">
                  0{i + 1} — Curated Pick
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B2C5B] leading-tight mb-4">
                  {h.name}
                </h3>
                <p className="text-lg leading-relaxed text-slate-700 mb-5">{h.blurb}</p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-7">
                  <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF8C00]" /> {h.distance}</span>
                  <span className="inline-flex items-center gap-2 font-semibold text-[#0B2C5B]">{h.price}</span>
                </div>
                <AffiliateButton href={h.href} variant="primary" testId={`${h.testId}-cta`}>
                  {h.cta}
                </AffiliateButton>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Mid-page email capture */}
      <EmailCapture source="hotels-page" variant="dark" />

      {/* Final CTA */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="container-site text-center max-w-3xl mx-auto">
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] mb-5 leading-tight">
            Compare all hotels near Universal Studios Bedford.
          </h2>
          <p className="text-lg text-slate-700 mb-8">
            See live prices, photos and guest reviews across every hotel within 10 miles of the park.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <AffiliateButton href={AFFILIATE.booking} variant="primary" testId="hotels-compare-booking-cta">
              Compare on Booking.com
            </AffiliateButton>
            <AffiliateButton href={AFFILIATE.expedia} variant="navy" testId="hotels-compare-expedia-cta">
              View on Expedia
            </AffiliateButton>
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/plan-your-trip" className="inline-flex items-center gap-2 text-[#0B2C5B] font-semibold border-b-2 border-[#FF8C00] pb-1" data-testid="hotels-to-plan-link">
              Next: plan your itinerary <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hotels;
